/**
 * AreaSelector
 *
 * Handles interactive area selection with visual feedback.
 * Supports area threshold validation and displays warnings for oversized selections.
 */

import * as THREE from 'three';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { Viewer } from '../../../../viewer/viewer';

export interface BoxStyle {
  border?: string; // CSS border (default: '2px dashed white')
  background?: string; // CSS background (default: 'rgba(100, 100, 100, 0.2)')
  warningBorder?: string; // Border when exceeds threshold (default: 'red')
  warningBackground?: string; // Background when exceeds threshold (default: 'rgba(255, 0, 0, 0.2)')
}

export interface AreaSelectorConfig {
  areaThresholdM2?: number; // Maximum recommended area (default: 50 m²)
  minAreaM2?: number; // Minimum area threshold (default: 0.5 m²)
  showLabel?: boolean; // Show area label (default: true)
  showWarning?: boolean; // Show warning tooltip when exceeds threshold (default: true)
  boxStyle?: BoxStyle; // Custom box styling
}

export class AreaSelector {
  private viewer: Viewer;
  private coordinateTransformer: CoordinateTransformer;
  private renderArea: HTMLElement;

  // Selection state
  private isSelectingArea = false;
  private selectionStart: THREE.Vector2 | null = null;
  private selectionStartWorld: THREE.Vector3 | null = null; // World space start position
  private currentMouse: THREE.Vector2 | null = null; // Track current mouse for camera updates
  private selectionAreaM2 = 0;
  private hasStartedDragging = false;
  private cameraHandler: (() => void) | null = null;

  // Thresholds
  private readonly AREA_THRESHOLD_M2: number;
  private readonly MIN_AREA_M2: number;
  private readonly DRAG_THRESHOLD_PX: number = 5; // Minimum pixels to drag before showing UI

  // Display options
  private readonly showLabel: boolean;
  private readonly showWarning: boolean;
  private readonly boxStyle: Required<BoxStyle>;

  // UI elements
  private selectionBox: HTMLDivElement;
  private areaLabel: HTMLDivElement | null = null;
  private areaWarningTooltip: HTMLDivElement | null = null;
  private updateAreaLabelOverride: ((mouse: THREE.Vector2) => void) | null = null;

  constructor(viewer: Viewer, coordinateTransformer: CoordinateTransformer, config: AreaSelectorConfig = {}) {
    this.viewer = viewer;
    this.renderArea = viewer.renderArea;
    this.coordinateTransformer = coordinateTransformer;
    this.AREA_THRESHOLD_M2 = config.areaThresholdM2 ?? 50;
    this.MIN_AREA_M2 = config.minAreaM2 ?? 0.5;

    // Display options
    this.showLabel = config.showLabel ?? true;
    this.showWarning = config.showWarning ?? true;
    this.boxStyle = {
      border: config.boxStyle?.border ?? '2px dashed white',
      background: config.boxStyle?.background ?? 'rgba(100, 100, 100, 0.2)',
      warningBorder: config.boxStyle?.warningBorder ?? 'red',
      warningBackground: config.boxStyle?.warningBackground ?? 'rgba(255, 0, 0, 0.2)',
    };

    // Create selection box element
    this.selectionBox = document.createElement('div');
    this.selectionBox.style.position = 'absolute';
    this.selectionBox.style.border = this.boxStyle.border;
    this.selectionBox.style.background = this.boxStyle.background;
    this.selectionBox.style.display = 'none';
    this.selectionBox.style.pointerEvents = 'none';

    // Attach selection box
    this.renderArea.appendChild(this.selectionBox);

    // Create area label (only if showLabel is true)
    if (this.showLabel) {
      this.areaLabel = document.createElement('div');
      this.areaLabel.style.position = 'absolute';
      this.areaLabel.style.background = 'rgba(0, 0, 0, 0.8)';
      this.areaLabel.style.color = 'white';
      this.areaLabel.style.padding = '8px 12px';
      this.areaLabel.style.borderRadius = '4px';
      this.areaLabel.style.fontFamily = 'monospace';
      this.areaLabel.style.fontSize = '14px';
      this.areaLabel.style.fontWeight = 'bold';
      this.areaLabel.style.pointerEvents = 'none';
      this.areaLabel.style.display = 'none';
      this.areaLabel.style.zIndex = '1001';
      this.renderArea.appendChild(this.areaLabel);
    }

    // Create area warning tooltip (only if showWarning is true)
    if (this.showWarning) {
      this.areaWarningTooltip = document.createElement('div');
      this.areaWarningTooltip.style.position = 'absolute';
      this.areaWarningTooltip.style.background = 'rgba(255, 0, 0, 0.8)';
      this.areaWarningTooltip.style.color = 'white';
      this.areaWarningTooltip.style.padding = '8px 12px';
      this.areaWarningTooltip.style.borderRadius = '4px';
      this.areaWarningTooltip.style.fontFamily = 'monospace';
      this.areaWarningTooltip.style.fontSize = '12px';
      this.areaWarningTooltip.style.fontWeight = 'bold';
      this.areaWarningTooltip.style.pointerEvents = 'none';
      this.areaWarningTooltip.style.display = 'none';
      this.areaWarningTooltip.style.zIndex = '1002';
      this.areaWarningTooltip.style.maxWidth = '250px';
      this.areaWarningTooltip.style.textAlign = 'center';
      this.areaWarningTooltip.innerHTML = this.viewer.translationService.t('Press <strong>Enter</strong> to confirm, accuracy may be reduced');
      this.renderArea.appendChild(this.areaWarningTooltip);
    }
  }

  /**
   * Check if currently selecting an area
   */
  isSelecting(): boolean {
    return this.isSelectingArea;
  }

  /**
   * Get the selection start position
   */
  getSelectionStart(): THREE.Vector2 | null {
    return this.selectionStart;
  }

  /**
   * Get the current selection area in square meters
   */
  getSelectionAreaM2(): number {
    return this.selectionAreaM2;
  }

  /**
   * Get area thresholds
   */
  getThresholds(): { areaThreshold: number; minArea: number } {
    return {
      areaThreshold: this.AREA_THRESHOLD_M2,
      minArea: this.MIN_AREA_M2,
    };
  }

  /**
   * Start area selection at the given mouse position
   */
  startSelection(mouse: THREE.Vector2): void {
    this.isSelectingArea = true;
    this.selectionStart = mouse.clone();
    this.hasStartedDragging = false;

    // Convert to world space for camera-independent tracking
    const worldPos = this.coordinateTransformer.screenToWorld(mouse);
    this.selectionStartWorld = worldPos || null;

    this.selectionBox.style.left = `${mouse.x}px`;
    this.selectionBox.style.top = `${mouse.y}px`;
    this.selectionBox.style.width = `0px`;
    this.selectionBox.style.height = `0px`;
    // Don't show the box yet - wait for drag threshold
    this.selectionBox.style.display = 'none';

    // Add camera update listener to redraw selection box during pan/zoom
    this.cameraHandler = () => {
      if (!this.isSelectingArea || !this.selectionStartWorld) return;

      // Reproject world position to current screen space
      const reprojected = this.coordinateTransformer.worldToScreen(this.selectionStartWorld);
      this.selectionStart = reprojected;

      // Redraw the selection box if we have a current mouse position
      if (this.hasStartedDragging && this.currentMouse) {
        this.updateSelection(this.currentMouse);
      }
    };

    this.viewer.addEventListener('camera_changed', this.cameraHandler);
    this.viewer.addEventListener('controls_changed', this.cameraHandler);
  }

  /**
   * Update selection during mouse move
   */
  updateSelection(mouse: THREE.Vector2): void {
    if (!this.isSelectingArea || !this.selectionStart) return;

    // Store current mouse for camera updates
    this.currentMouse = mouse.clone();

    // Check if we've exceeded the drag threshold
    if (!this.hasStartedDragging) {
      const distance = mouse.distanceTo(this.selectionStart);
      if (distance > this.DRAG_THRESHOLD_PX) {
        this.hasStartedDragging = true;
        this.selectionBox.style.display = 'block';
      } else {
        // Still below threshold - don't show anything
        return;
      }
    }

    const width = mouse.x - this.selectionStart.x;
    const height = mouse.y - this.selectionStart.y;

    this.selectionBox.style.width = `${Math.abs(width)}px`;
    this.selectionBox.style.height = `${Math.abs(height)}px`;
    this.selectionBox.style.left = `${width < 0 ? mouse.x : this.selectionStart.x}px`;
    this.selectionBox.style.top = `${height < 0 ? mouse.y : this.selectionStart.y}px`;

    // Calculate and update area label
    this.updateAreaLabel(mouse);
  }

  /**
   * Cancel area selection
   */
  cancelSelection(): void {
    this.isSelectingArea = false;
    this.hasStartedDragging = false;
    this.hideSelectionUI();
    this.selectionStart = null;
    this.selectionStartWorld = null;
    this.currentMouse = null;
    this.removeCameraHandler();
  }

  /**
   * Release control without hiding UI (for when BoxEditMode takes over)
   */
  releaseControl(): void {
    this.isSelectingArea = false;
    this.hasStartedDragging = false;
    this.selectionStart = null;
    this.selectionStartWorld = null;
    this.currentMouse = null;
    this.removeCameraHandler();
    // Don't hide UI - BoxEditMode will manage it
  }

  /**
   * Remove camera event handler
   */
  private removeCameraHandler(): void {
    if (this.cameraHandler) {
      this.viewer.removeEventListener('camera_changed', this.cameraHandler);
      this.viewer.removeEventListener('controls_changed', this.cameraHandler);
      this.cameraHandler = null;
    }
  }

  /**
   * Hide selection UI elements
   */
  hideSelectionUI(): void {
    this.selectionBox.style.display = 'none';
    if (this.areaLabel) {
      this.areaLabel.style.display = 'none';
    }
    if (this.areaWarningTooltip) {
      this.areaWarningTooltip.style.display = 'none';
    }
  }

  /**
   * Get the selection box element (for reading dimensions in edit mode)
   */
  getSelectionBoxElement(): HTMLDivElement {
    return this.selectionBox;
  }

  /**
   * Update area label with calculated area
   */
  private updateAreaLabel(mouse: THREE.Vector2): void {
    if (!this.selectionStart) return;

    // Calculate area in square meters using world-space calculation
    const areaM2 = this.coordinateTransformer.calculateBoxAreaM2(this.selectionStart, mouse);
    this.selectionAreaM2 = areaM2;
    const exceedsThreshold = areaM2 > this.AREA_THRESHOLD_M2;

    // Update box color based on area threshold (using custom styles)
    this.selectionBox.style.border = exceedsThreshold ? this.boxStyle.warningBorder : this.boxStyle.border;
    this.selectionBox.style.background = exceedsThreshold ? this.boxStyle.warningBackground : this.boxStyle.background;

    // Show area label in the middle of the box (only if enabled)
    if (this.areaLabel) {
      const centerX = (this.selectionStart.x + mouse.x) / 2;
      const centerY = (this.selectionStart.y + mouse.y) / 2;

      this.areaLabel.textContent = `${areaM2.toFixed(2)} m²`;
      this.areaLabel.style.backgroundColor = exceedsThreshold ? 'rgba(255, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.8)';
      this.areaLabel.style.display = 'block';
      this.areaLabel.style.left = `${centerX}px`;
      this.areaLabel.style.top = `${centerY}px`;
      this.areaLabel.style.transform = 'translate(-50%, -50%)';

      // Show warning tooltip below the area label when threshold is exceeded (only if enabled)
      if (this.areaWarningTooltip) {
        if (exceedsThreshold) {
          this.areaWarningTooltip.style.display = 'block';
          this.areaWarningTooltip.style.left = `${centerX}px`;
          this.areaWarningTooltip.style.top = `${centerY + 30}px`;
          this.areaWarningTooltip.style.transform = 'translate(-50%, 0)';
        } else {
          this.areaWarningTooltip.style.display = 'none';
        }
      }
    }

    if (this.updateAreaLabelOverride) {
      this.updateAreaLabelOverride(mouse);
    }
  }

  /**
   * Get the area label element (for customizing label content in edit modes)
   */
  getAreaLabel(): HTMLDivElement | null {
    return this.areaLabel;
  }

  /**
   * Get the area warning tooltip element
   */
  getAreaWarningTooltip(): HTMLDivElement | null {
    return this.areaWarningTooltip;
  }

  /**
   * Override the updateAreaLabel callback with a custom function.
   * The override receives the mouse position and can modify the label.
   */
  setUpdateAreaLabelOverride(override: ((mouse: THREE.Vector2) => void) | null): void {
    this.updateAreaLabelOverride = override;
  }

  /**
   * Dispose and clean up
   */
  dispose(): void {
    this.selectionBox.remove();
    if (this.areaLabel) {
      this.areaLabel.remove();
    }
    if (this.areaWarningTooltip) {
      this.areaWarningTooltip.remove();
    }
  }
}
